import { supabase } from './supabase';
import customerService from './customerService';
import emailService from './emailService';
import reviewService from './reviewService';
import campaignService from './campaignService';

class ReviewAutomationService {
  constructor() {
    // Default settings - can be overridden by business settings
    this.defaultSettings = {
      autoSendReviews: true,
      sendDelay: 24, // hours after service completion
      reminderDelay: 72, // hours after first email
      maxReminders: 1, // Fixed to 1 reminder only
      emailEnabled: true,
      businessName: 'ServiceFlow Pro',
      googleReviewUrl: '',
      yelpUrl: '',
      facebookUrl: '',
      businessLogoUrl: ''
    };
  }

  // Load business settings (could be from local storage, API, or database)
  getBusinessSettings() {
    try {
      const savedSettings = localStorage.getItem('reviewSettings');
      if (savedSettings) {
        return { ...this.defaultSettings, ...JSON.parse(savedSettings) };
      }
      return this.defaultSettings;
    } catch (error) {
      console.error('Error loading business settings:', error);
      return this.defaultSettings;
    }
  }

  // Save business settings
  saveBusinessSettings(settings) {
    try {
      const updatedSettings = { ...this.getBusinessSettings(), ...settings };
      localStorage.setItem('reviewSettings', JSON.stringify(updatedSettings));
      return { success: true };
    } catch (error) {
      console.error('Error saving business settings:', error);
      return { success: false, error: 'Failed to save settings' };
    }
  }

  // Legacy appointment-based helper functions (no longer used)

  // Send review request email
  async sendReviewRequest(customerId, triggerType = 'manual') {
    try {
      // 1. Check if we can even send a request using the new, smarter logic
      const { canSend, reason } = await customerService.checkCanSendRequest(customerId);

      if (!canSend) {
        // If we can't send, throw an error with the specific reason
        throw new Error(reason);
      }

      // 2. Fetch all necessary data in one go
      const { data, error: dataError } = await this.getReviewRequestData(customerId);
      if (dataError) {
        throw new Error(dataError.message || 'Failed to fetch required data.');
      }

      // 3. Send the email
      const emailResult = await emailService.sendReviewRequest(
        data.customer,
        data.campaign,
        data.organization
      );

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send email.');
      }

      // 4. Create the review request record
      const reviewRequest = {
        organization_id: data.organization.id,
        customer_id: customerId,
        pipeline_stage: data.customer.pipeline_stage,
        trigger_type: triggerType,
        status: 'sent',
        method: 'email',
        recipient_email: data.customer.email,
        scheduled_send_at: new Date().toISOString(),
        reminder_due_at: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
        max_reminders: 1,
        campaign_id: data.campaign.id,
        campaign_config: data.campaign,
        created_at: new Date().toISOString()
      };

      const { data: newRequest, error: requestError } = await supabase
        .from('review_requests')
        .insert([reviewRequest])
        .select()
        .single();

      if (requestError) {
        throw new Error(requestError.message || 'Failed to create review request.');
      }

      // 5. Log the review request
      await this.logReviewRequest(customerId, null, null, triggerType, 'sent');

      return { success: true, message: 'Review request sent successfully!' };
    } catch (error) {
      console.error('Failed to send review request:', error);
      return { success: false, error: error.message || 'An unknown error occurred' };
    }
  }

  // Function to gather all data needed for a review request
  async getReviewRequestData(customerId) {
    try {
      const { data: customer, error: customerError } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', customerId)
        .single();

      if (customerError) {
        throw new Error(customerError.message || 'Failed to fetch customer data.');
      }

      const campaign = await campaignService.getCampaign(customer.organization_id);
      if (!campaign) {
        throw new Error('No campaign found for this organization.');
      }

      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', customer.organization_id)
        .single();

      if (orgError) {
        throw new Error(orgError.message || 'Failed to fetch organization data.');
      }

      return { data: { customer, campaign, organization }, error: null };
    } catch (error) {
      console.error('Error fetching review request data:', error);
      return { data: null, error: error.message || 'An unknown error occurred' };
    }
  }

  // Send review reminder email (legacy method - not supported)
  async sendReviewReminder(appointmentId) {
    // Appointment-based review reminders are not supported in ServiceReviewAI
    return { 
      success: false, 
      error: 'Appointment-based review reminders are not supported. Use customer-based review automation instead.' 
    };
  }

  // Process completed appointments for automated review requests (legacy method - not supported)
  async processCompletedAppointments() {
    // Appointment-based processing is not supported in ServiceReviewAI
    return { 
      success: false, 
      error: 'Appointment-based processing is not supported. Use customer-based review automation instead.' 
    };
  }

  // Log review request for tracking
  logReviewRequest(data) {
    try {
      const logs = this.getReviewLogs();
      logs.push({
        ...data,
        id: Date.now().toString()
      });
      
      // Keep only last 100 logs
      const recentLogs = logs.slice(-100);
      localStorage.setItem('reviewLogs', JSON.stringify(recentLogs));
    } catch (error) {
      console.error('Error logging review request:', error);
    }
  }

  // Get review request logs
  getReviewLogs() {
    try {
      const logs = localStorage.getItem('reviewLogs');
      return logs ? JSON.parse(logs) : [];
    } catch (error) {
      console.error('Error getting review logs:', error);
      return [];
    }
  }

  // Get review statistics
  getReviewStatistics() {
    try {
      const logs = this.getReviewLogs();
      const now = new Date();
      const last30Days = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

      const recentLogs = logs.filter(log => new Date(log.sentAt) >= last30Days);

      return {
        total: logs.length,
        last30Days: recentLogs.length,
        byType: {
          initial: recentLogs.filter(log => log.type === 'initial').length,
          reminder: recentLogs.filter(log => log.type === 'reminder').length
        },
        lastSent: logs.length > 0 ? logs[logs.length - 1].sentAt : null
      };
    } catch (error) {
      console.error('Error getting review statistics:', error);
      return {
        total: 0,
        last30Days: 0,
        byType: { initial: 0, reminder: 0 },
        lastSent: null
      };
    }
  }

  // Test email functionality
  async testEmailService(testEmail) {
    try {
      const result = await emailService.sendTestEmail(testEmail);
      return result;
    } catch (error) {
      return { 
        success: false, 
        error: 'Email service test failed' 
      };
    }
  }

  // Manual trigger for specific appointment
  async triggerManualReviewRequest(appointmentId, force = false) {
    try {
      if (force) {
        // Appointment-based manual review requests are not supported in ServiceReviewAI
        return { 
          success: false, 
          error: 'Appointment-based manual review requests are not supported. Use customer-based review automation instead.' 
        };

        const appointment = appointmentResult.data[0];
        const settings = this.getBusinessSettings();

        const emailResult = await emailService.sendReviewRequest(
          appointment,
          appointment.contact,
          settings
        );

        if (emailResult.success) {
          this.logReviewRequest({
            appointmentId,
            customerEmail: appointment.contact.email,
            emailId: emailResult.emailId,
            sentAt: new Date().toISOString(),
            type: 'manual'
          });
        }

        return emailResult;
      } else {
        return await this.sendReviewRequest(appointmentId);
      }
    } catch (error) {
      console.error('Error triggering manual review request:', error);
      return { 
        success: false, 
        error: 'Failed to send manual review request' 
      };
    }
  }

  // Check if customer should receive review request when moved to a stage
  async handleStageChange(customerId, newStageId, stageName) {
    try {
      console.log('🔄 handleStageChange called:', { customerId, newStageId, stageName });
      
      // Check if the new stage triggers reviews
      const { data: stage, error: stageError } = await supabase
        .from('pipeline_stages')
        .select('triggers_review')
        .eq('id', newStageId)
        .single();

      if (stageError) {
        console.error('❌ Error checking stage:', stageError);
        return;
      }

      console.log('📊 Stage data:', stage);

      // If stage doesn't trigger reviews, exit early
      if (!stage?.triggers_review) {
        console.log('⏭️ Stage does not trigger reviews, skipping');
        return;
      }

      console.log('✅ Stage triggers reviews, checking for existing reviews...');

      // Enhanced check for existing reviews
      const reviewCheck = await customerService.checkExistingReview(customerId);

      console.log('🔍 Enhanced review check:', reviewCheck);

      // If customer has completed a review, don't send automatic request
      if (reviewCheck.hasCompletedReview) {
        console.log(`✅ Customer ${customerId} has already completed a review (${reviewCheck.latestCompletedReview.rating} stars). Skipping automatic trigger.`);
        
        // Log the prevented duplicate
        await this.logReviewRequest(customerId, newStageId, stageName, 'automatic', 'skipped_completed_review');
        return {
          success: false,
          reason: 'completed_review',
          message: 'Customer has already completed a review',
          completedReview: reviewCheck.latestCompletedReview
        };
      }

      // If customer has active (pending/submitted) requests, don't send automatic request
      if (reviewCheck.activeRequests.length > 0) {
        console.log(`⚠️ Customer ${customerId} has active review request(s). Skipping automatic trigger.`);
        
        // Log the prevented duplicate
        await this.logReviewRequest(customerId, newStageId, stageName, 'automatic', 'skipped_active_request');
        return {
          success: false,
          reason: 'active_request',
          message: 'Customer has pending review request',
          activeRequest: reviewCheck.activeRequests[0]
        };
      }

      // If customer has recent requests (within 24 hours), don't send automatic request
      if (reviewCheck.recentRequests.length > 0) {
        console.log(`⚠️ Customer ${customerId} has recent review request (within 24 hours). Skipping automatic trigger.`);
        
        // Log the prevented duplicate
        await this.logReviewRequest(customerId, newStageId, stageName, 'automatic', 'skipped_recent_request');
        return {
          success: false,
          reason: 'recent_request',
          message: 'Review request sent recently (within 24 hours)',
          recentRequest: reviewCheck.recentRequests[0]
        };
      }

      console.log('🚀 No conflicts found. Sending automatic review request...');
      // Send review request
      const result = await this.sendAutomaticReviewRequest(customerId, newStageId, stageName);
      
      return {
        success: true,
        reason: 'sent_automatic',
        message: 'Automatic review request sent successfully',
        result
      };

    } catch (error) {
      console.error('❌ Error in handleStageChange:', error);
      return {
        success: false,
        reason: 'error',
        message: error.message,
        error
      };
    }
  }

  // Send automatic review request
  async sendAutomaticReviewRequest(customerId, stageId, stageName) {
    try {
      console.log('📧 sendAutomaticReviewRequest called:', { customerId, stageId, stageName });
      
      // Get customer details
      const { data: customer, error: customerError } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', customerId)
        .single();

      if (customerError) {
        console.error('❌ Error fetching customer:', customerError);
        return;
      }

      console.log('👤 Customer data:', { id: customer.id, name: customer.name, email: customer.email });

      if (!customer.email) {
        console.log(`⚠️ Customer ${customerId} has no email, skipping review request`);
        return;
      }

      // ✅ NEW: Get campaign configuration for this organization
      let campaignData = null;
      try {
        campaignData = await campaignService.getCampaign(customer.organization_id);
        if (!campaignData) {
          console.log('⚠️ No campaign found, using defaults');
          campaignData = campaignService.getDefaultCampaign();
        }
        console.log('📋 Using campaign config:', { 
          questions: campaignData.questions?.length || 0,
          emailSubject: campaignData.email_subject,
          threshold: campaignData.positive_threshold 
        });
      } catch (error) {
        console.error('❌ Error fetching campaign, using defaults:', error);
        campaignData = campaignService.getDefaultCampaign();
      }

      // Get business settings for delays
      const settings = this.getBusinessSettings();
      const sendDelayHours = settings.sendDelay || 24; // Hours after service completion
      const reminderDelayHours = settings.reminderDelay || 72; // Hours after first email

      console.log('⏰ Delay settings:', { sendDelayHours, reminderDelayHours });

      // Calculate when to send the email (delayed send)
      const scheduledSendTime = new Date(Date.now() + sendDelayHours * 60 * 60 * 1000);
      const reminderDueTime = new Date(scheduledSendTime.getTime() + reminderDelayHours * 60 * 60 * 1000);

      console.log('📅 Scheduled times:', {
        now: new Date().toISOString(),
        scheduledSend: scheduledSendTime.toISOString(),
        reminderDue: reminderDueTime.toISOString()
      });

      // Create review request record with scheduled send time and campaign config
      const reviewRequest = {
        organization_id: customer.organization_id,
        customer_id: customerId,
        pipeline_stage: stageName,
        trigger_type: 'automatic',
        status: 'scheduled', // Mark as scheduled, not sent yet
        method: 'email',
        recipient_email: customer.email,
        scheduled_send_at: scheduledSendTime.toISOString(), // When to actually send
        reminder_due_at: reminderDueTime.toISOString(),
        max_reminders: 1, // Fixed to 1 reminder only
        // ✅ NEW: Include campaign configuration
        campaign_id: campaignData.id || null,
        campaign_config: {
          questions: campaignData.questions || [],
          positive_threshold: campaignData.positive_threshold || 4,
          thank_you_pages: campaignData.thank_you_pages || {},
          selected_review_platform: campaignData.selected_review_platform || 'google',
          platform_urls: campaignData.platform_urls || { google: '', yelp: '', facebook: '' }
        },
        created_at: new Date().toISOString()
      };

      const { data: newRequest, error: requestError } = await supabase
        .from('review_requests')
        .insert([reviewRequest])
        .select()
        .single();

      if (requestError) {
        console.error('Error creating review request:', requestError);
        return;
      }

      console.log('✅ Review request scheduled:', {
        id: newRequest.id,
        scheduledFor: scheduledSendTime.toISOString(),
        delayHours: sendDelayHours
      });

      // Log the scheduled request
      await this.logReviewRequest(customerId, stageId, stageName, 'automatic', 'scheduled');

      console.log(`📋 Automatic review request scheduled for customer ${customerId} (${customer.email}) to be sent in ${sendDelayHours} hours`);

      return {
        success: true,
        scheduled: true,
        scheduledSendTime: scheduledSendTime.toISOString(),
        delayHours: sendDelayHours,
        requestId: newRequest.id
      };

    } catch (error) {
      console.error('Error scheduling automatic review request:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Send manual review request with smart eligibility check
  async sendManualReviewRequest(customerId, stageId, stageName, forceOverride = false, isFollowUp = false) {
    try {
      console.log('📧 sendManualReviewRequest called:', { customerId, stageId, stageName, forceOverride, isFollowUp });
      
      // Use our new smart eligibility check (unless force override)
      if (!forceOverride) {
        const { canSend, reason } = await customerService.checkCanSendRequest(customerId);
        console.log('🔍 Manual review eligibility check:', { canSend, reason });

        if (!canSend) {
          console.log('⚠️ Cannot send review request:', reason);
          return {
            success: false,
            error: reason,
            message: reason
          };
        }
      }

      // Get customer details
      const { data: customer, error: customerError } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', customerId)
        .single();

      if (customerError) {
        console.error('❌ Error fetching customer:', customerError);
        return {
          success: false,
          error: 'Customer not found'
        };
      }

      console.log('👤 Customer data:', { id: customer.id, name: customer.name, email: customer.email });

      if (!customer.email) {
        console.log(`⚠️ Customer ${customerId} has no email`);
        return {
          success: false,
          error: 'Customer has no email address'
        };
      }

      // ✅ NEW: Get campaign configuration for this organization  
      let campaignData = null;
      try {
        campaignData = await campaignService.getCampaign(customer.organization_id);
        if (!campaignData) {
          console.log('⚠️ No campaign found, using defaults');
          campaignData = campaignService.getDefaultCampaign();
        }
        console.log('📋 Manual request using campaign config:', { 
          questions: campaignData.questions?.length || 0,
          emailSubject: campaignData.email_subject,
          threshold: campaignData.positive_threshold 
        });
      } catch (error) {
        console.error('❌ Error fetching campaign, using defaults:', error);
        campaignData = campaignService.getDefaultCampaign();
      }

      // Get reminder delay from settings
      const settings = this.getBusinessSettings();
      const reminderDelayHours = settings.reminderDelay || 72;
      
      // Determine trigger type and reminder count
      let triggerType = 'manual';
      let reminderCount = 0;
      
      if (isFollowUp) {
        triggerType = 'follow_up';
        // Get the existing request to increment reminder count
        const { data: existingRequests } = await supabase
          .from('review_requests')
          .select('reminder_count')
          .eq('customer_id', customerId)
          .order('sent_at', { ascending: false })
          .limit(1);
        
        if (existingRequests && existingRequests.length > 0) {
          reminderCount = (existingRequests[0].reminder_count || 0) + 1;
        }
      }
      
      // Create review request record with campaign configuration
      const reviewRequest = {
        organization_id: customer.organization_id,
        customer_id: customerId,
        pipeline_stage: stageName,
        trigger_type: triggerType,
        status: 'pending',
        method: 'email',
        recipient_email: customer.email,
        sent_at: new Date().toISOString(),
        reminder_due_at: new Date(Date.now() + reminderDelayHours * 60 * 60 * 1000).toISOString(),
        reminder_count: reminderCount,
        max_reminders: 1, // Fixed to 1 reminder only
        duplicate_prevented: false,
        // ✅ NEW: Include campaign configuration for manual requests too
        campaign_id: campaignData.id || null,
        campaign_config: {
          questions: campaignData.questions || [],
          positive_threshold: campaignData.positive_threshold || 4,
          thank_you_pages: campaignData.thank_you_pages || {},
          selected_review_platform: campaignData.selected_review_platform || 'google',
          platform_urls: campaignData.platform_urls || { google: '', yelp: '', facebook: '' }
        },
        notes: isFollowUp ? 'Manual follow-up request' : (forceOverride ? 'Manual request with force override' : 'Manual request')
      };

      console.log('📝 Review request data:', reviewRequest);

      const { data: newRequest, error: requestError } = await supabase
        .from('review_requests')
        .insert([reviewRequest])
        .select()
        .single();

      if (requestError) {
        console.error('❌ Error creating review request:', requestError);
        return {
          success: false,
          error: `Failed to create review request: ${requestError.message || requestError}`
        };
      }

      console.log('✅ Review request created:', newRequest);

      // Create unique review token
      const tokenResult = await reviewService.createReviewToken(
        newRequest.id,
        customerId,
        customer.email
      );

      if (!tokenResult.success) {
        console.error('❌ Failed to create review token:', tokenResult.error);
        return {
          success: false,
          error: `Failed to create review token: ${tokenResult.error}`
        };
      }

      const reviewToken = tokenResult.data.token;
      console.log('🔑 Review token created:', reviewToken);

      console.log('📧 Sending email...');
      
      // Get current user's reply-to email
      const { data: { user } } = await supabase.auth.getUser();
      let replyToEmail = null;
      
      if (user) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('reply_to_email, email')
          .eq('id', user.id)
          .single();
        
        replyToEmail = userProfile?.reply_to_email || userProfile?.email;
      }
      
      // Send email with token and campaign configuration
      const emailResult = await emailService.sendReviewEmail({
        customerId: customerId,
        customerEmail: customer.email,
        customerName: customer.name || customer.first_name + ' ' + customer.last_name,
        reviewToken: reviewToken,
        replyToEmail: replyToEmail,
        // ✅ NEW: Include campaign configuration in email
        campaignConfig: campaignData,
        appointmentData: {
          service_type: 'Service Completion',
          scheduled_date: new Date().toISOString(),
          customer_name: customer.name || customer.first_name + ' ' + customer.last_name,
          customer_email: customer.email,
          stage_name: stageName
        }
      });

      console.log('📧 Email result:', emailResult);

      console.log('🔄 Updating review request status...');
      
      // Update request status based on email result
      const updateData = {
        status: emailResult.success ? 'submitted' : 'failed',
        sent_at: new Date().toISOString()
      };

      if (emailResult.emailId) {
        updateData.email_id = emailResult.emailId;
      }

      console.log('📝 Update data:', updateData);

      await supabase
        .from('review_requests')
        .update(updateData)
        .eq('id', newRequest.id);

      // ✅ NEW: Schedule automatic follow-up if automation is enabled and this is not already a follow-up
      if (emailResult.success && !isFollowUp && settings.autoSendReviews) {
        await this.scheduleAutomaticFollowUp(newRequest.id, customerId, settings);
      }

      console.log('✅ Manual review request completed successfully');

      return {
        success: true,
        reviewRequest: newRequest,
        emailResult,
        reviewToken,
        message: forceOverride 
          ? 'Review request sent successfully (override confirmed)'
          : 'Review request sent successfully'
      };

    } catch (error) {
      console.error('❌ Error sending manual review request:', error);
      return {
        success: false,
        error: `Failed to send review request: ${error.message || error}`
      };
    }
  }

  // ✅ NEW: Schedule automatic follow-up for a review request
  async scheduleAutomaticFollowUp(requestId, customerId, settings) {
    try {
      console.log('📅 Scheduling automatic follow-up for request:', requestId);
      
      // Calculate when to send the follow-up
      const followUpDelayHours = settings.reminderDelay || 72;
      const scheduledFollowUpTime = new Date(Date.now() + followUpDelayHours * 60 * 60 * 1000);
      
      // Get customer details for the follow-up
      const { data: customer, error: customerError } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', customerId)
        .single();

      if (customerError || !customer) {
        console.error('❌ Error fetching customer for follow-up scheduling:', customerError);
        return { success: false, error: 'Customer not found for follow-up' };
      }

      // Create scheduled follow-up request
      const followUpRequest = {
        customer_id: customerId,
        pipeline_stage: 'Follow-up',
        trigger_type: 'automatic',
        status: 'scheduled',
        method: 'email',
        recipient_email: customer.email,
        scheduled_send_at: scheduledFollowUpTime.toISOString(),
        reminder_count: 1, // This is the first follow-up
        max_reminders: 1, // Fixed to 1 reminder only
        parent_request_id: requestId,
        notes: `Automatic follow-up scheduled for ${followUpDelayHours} hours after initial request`,
        created_at: new Date().toISOString()
      };

      console.log('📝 Creating scheduled follow-up:', followUpRequest);

      const { data: scheduledRequest, error: scheduleError } = await supabase
        .from('review_requests')
        .insert([followUpRequest])
        .select()
        .single();

      if (scheduleError) {
        console.error('❌ Error scheduling follow-up:', scheduleError);
        return { success: false, error: scheduleError.message };
      }

      console.log(`✅ Follow-up scheduled successfully for ${followUpDelayHours} hours`, {
        followUpId: scheduledRequest.id,
        scheduledFor: scheduledFollowUpTime.toISOString()
      });

      return {
        success: true,
        followUpId: scheduledRequest.id,
        scheduledFor: scheduledFollowUpTime.toISOString(),
        delayHours: followUpDelayHours
      };

    } catch (error) {
      console.error('❌ Error in scheduleAutomaticFollowUp:', error);
      return { success: false, error: error.message };
    }
  }

  // ✅ NEW: Process and mark expired review requests
  async processExpiredRequests() {
    try {
      console.log('🔄 Processing expired review requests...');
      
      const settings = this.getBusinessSettings();
      const expireAfterHours = 30 * 24; // 30 days in hours (could be configurable)
      const expireBeforeTime = new Date(Date.now() - expireAfterHours * 60 * 60 * 1000);
      
      console.log(`⏰ Checking for requests older than ${expireAfterHours} hours (before ${expireBeforeTime.toISOString()})`);

      // Find requests that should be expired:
      // - Status is 'submitted' or 'follow_up_sent' 
      // - Not already completed or failed
      // - Sent more than expireAfterHours ago
      // - Not already marked as expired
      const { data: expiredRequests, error: fetchError } = await supabase
        .from('review_requests')
        .select('*')
        .in('status', ['submitted', 'follow_up_sent'])
        .eq('review_completed', false)
        .lt('sent_at', expireBeforeTime.toISOString())
        .neq('status', 'expired');

      if (fetchError) {
        console.error('❌ Error fetching expired requests:', fetchError);
        return { success: false, error: fetchError.message };
      }

      if (!expiredRequests || expiredRequests.length === 0) {
        console.log('📋 No requests found that need to be expired');
        return { success: true, processed: 0 };
      }

      console.log(`📋 Found ${expiredRequests.length} requests to mark as expired`);

      let successCount = 0;
      let failureCount = 0;

      // Mark each request as expired
      for (const request of expiredRequests) {
        try {
          console.log(`⏳ Marking request ${request.id} as expired (customer: ${request.customer_id})`);
          
          const { error: updateError } = await supabase
            .from('review_requests')
            .update({
              status: 'expired',
              expired_at: new Date().toISOString(),
              notes: request.notes ? 
                `${request.notes}\n[AUTO-EXPIRED: Request expired after ${expireAfterHours} hours]` : 
                `[AUTO-EXPIRED: Request expired after ${expireAfterHours} hours]`
            })
            .eq('id', request.id);

          if (updateError) {
            console.error(`❌ Error marking request ${request.id} as expired:`, updateError);
            failureCount++;
          } else {
            console.log(`✅ Request ${request.id} marked as expired`);
            successCount++;
          }

        } catch (error) {
          console.error(`❌ Error processing expiration for request ${request.id}:`, error);
          failureCount++;
        }
      }

      console.log(`📊 Processed ${expiredRequests.length} expired requests: ${successCount} marked as expired, ${failureCount} failed`);

      return {
        success: true,
        processed: expiredRequests.length,
        expired: successCount,
        failed: failureCount,
        expireAfterHours
      };

    } catch (error) {
      console.error('❌ Error processing expired requests:', error);
      return { success: false, error: error.message };
    }
  }

  // Log review request for analytics
  async logReviewRequest(customerId, stageId, stageName, triggerType, status) {
    try {
      const logData = {
        customer_id: customerId,
        pipeline_stage: stageName,
        trigger_type: triggerType,
        status: status,
        duplicate_prevented: status === 'duplicate_prevented',
        sent_at: new Date().toISOString()
      };

      await supabase
        .from('review_requests')
        .insert([logData]);

    } catch (error) {
      console.error('Error logging review request:', error);
    }
  }

  // Get review analytics for organization
  async getReviewAnalytics(organizationId, dateRange = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const { data, error } = await supabase
        .from('review_requests')
        .select(`
          *,
          contacts!inner(organization_id)
        `)
        .eq('contacts.organization_id', organizationId)
        .gte('sent_at', startDate.toISOString())
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('Error fetching review analytics:', error);
        return null;
      }

      // Calculate analytics
      const analytics = {
        total_requests: data.length,
        automatic_requests: data.filter(r => r.trigger_type === 'automatic').length,
        manual_requests: data.filter(r => r.trigger_type === 'manual').length,
        sent_successfully: data.filter(r => r.status === 'submitted').length,
        pending_requests: data.filter(r => r.status === 'pending').length,
        duplicates_prevented: data.filter(r => r.duplicate_prevented).length,
        responded: data.filter(r => r.status === 'responded').length,
        response_rate: data.filter(r => r.status === 'submitted').length > 0 
          ? (data.filter(r => r.status === 'responded').length / data.filter(r => r.status === 'submitted').length * 100).toFixed(1)
          : 0
      };

      return {
        analytics,
        requests: data
      };

    } catch (error) {
      console.error('Error getting review analytics:', error);
      return null;
    }
  }

  // Automated reminder scheduler (can be called periodically)
  async scheduleAndProcessReminders() {
    try {
      console.log('🔄 Starting automated reminder processing...');
      
      const settings = this.getBusinessSettings();
      
      if (!settings.emailEnabled) {
        console.log('📧 Email is disabled, skipping reminder processing');
        return { success: false, error: 'Email disabled' };
      }

      // Process any pending reminders
      const reminderResult = await this.processReminders();
      
      if (!reminderResult.success) {
        console.error('Failed to process reminders:', reminderResult.error);
        return reminderResult;
      }

      console.log(`✅ Reminder processing completed: ${reminderResult.processed} sent, ${reminderResult.errors} errors`);
      
      return {
        success: true,
        processed: reminderResult.processed,
        errors: reminderResult.errors,
        message: `Processed ${reminderResult.processed} reminders`
      };
    } catch (error) {
      console.error('Error in scheduleAndProcessReminders:', error);
      return { success: false, error: 'Failed to process reminders' };
    }
  }

  // Initialize automatic reminder processing
  initializeReminderAutomation() {
    try {
      // Clear any existing interval
      if (this.reminderInterval) {
        clearInterval(this.reminderInterval);
      }

      const settings = this.getBusinessSettings();
      
      // Check if automation is enabled
      if (!settings.autoSendReviews) {
        console.log('⏸️ Review automation is disabled');
        return { success: false, message: 'Review automation is disabled' };
      }

      // Set up interval to check for scheduled requests and reminders every 5 minutes
      this.reminderInterval = setInterval(async () => {
        try {
          console.log('⏰ Automated processing - checking for scheduled requests and reminders...');
          
          // Process scheduled review requests
          const scheduledResult = await this.processScheduledReviewRequests();
          if (scheduledResult.processed > 0) {
            console.log(`📧 Automated: sent ${scheduledResult.sent} scheduled review requests`);
          }
          
          // Process reminders
          const reminderResult = await this.processReminders();
          if (reminderResult.processed > 0) {
            console.log(`🔔 Automated: sent ${reminderResult.processed} reminders`);
          }
          
          // ✅ NEW: Process expired requests
          const expirationResult = await this.processExpiredRequests();
          if (expirationResult.expired > 0) {
            console.log(`⏳ Automated: marked ${expirationResult.expired} requests as expired`);
          }
          
        } catch (error) {
          console.error('❌ Error in automated processing:', error);
        }
      }, 5 * 60 * 1000); // Check every 5 minutes

      console.log('✅ Review automation initialized - checking every 5 minutes for scheduled requests and reminders');
      
      return { 
        success: true, 
        message: 'Review automation initialized - checking every 5 minutes for scheduled requests and reminders' 
      };
    } catch (error) {
      console.error('Error initializing reminder automation:', error);
      return { 
        success: false, 
        error: 'Failed to initialize automation' 
      };
    }
  }

  // Trigger reminder processing manually
  async triggerReminderProcessing() {
    try {
      console.log('🚀 Triggering reminder and scheduled review processing...');
      
      // Process scheduled review requests first
      const scheduledResult = await this.processScheduledReviewRequests();
      console.log('📧 Scheduled requests result:', scheduledResult);
      
      // Then process reminders
      const reminderResult = await this.processReminders();
      console.log('🔔 Reminders result:', reminderResult);
      
      // ✅ NEW: Process expired requests
      const expirationResult = await this.processExpiredRequests();
      console.log('⏳ Expiration result:', expirationResult);
      
      return {
        success: true,
        scheduled: scheduledResult,
        reminders: reminderResult,
        expired: expirationResult,
        message: `Processed ${scheduledResult.processed || 0} scheduled requests, ${reminderResult.processed || 0} reminders, and ${expirationResult.expired || 0} expired requests`
      };
    } catch (error) {
      console.error('Error triggering processing:', error);
      return { 
        success: false, 
        error: 'Failed to trigger processing' 
      };
    }
  }

  // Get reminder statistics
  async getReminderStatistics() {
    try {
      const { data: stats, error } = await supabase
        .from('review_requests')
        .select(`
          status,
          reminder_count,
          review_completed,
          created_at
        `)
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()); // Last 30 days

      if (error) {
        return { success: false, error: error.message };
      }

      const totalRequests = stats.length;
      const completedReviews = stats.filter(s => s.review_completed).length;
      const pendingReminders = stats.filter(s => !s.review_completed && s.status === 'submitted').length;
      const totalReminders = stats.reduce((sum, s) => sum + (s.reminder_count || 0), 0);
      const avgRemindersPerRequest = totalRequests > 0 ? (totalReminders / totalRequests).toFixed(2) : 0;
      const completionRate = totalRequests > 0 ? ((completedReviews / totalRequests) * 100).toFixed(1) : 0;

      return {
        success: true,
        data: {
          totalRequests,
          completedReviews,
          pendingReminders,
          totalReminders,
          avgRemindersPerRequest,
          completionRate: `${completionRate}%`,
          period: 'Last 30 days'
        }
      };
    } catch (error) {
      console.error('Error getting reminder statistics:', error);
      return { success: false, error: 'Failed to get reminder statistics' };
    }
  }

  // Update reminder settings
  updateReminderSettings(newSettings) {
    const currentSettings = this.getBusinessSettings();
    
    // Update the settings - force maxReminders to always be 1
    const updatedSettings = {
      ...currentSettings,
      reminderDelay: newSettings.reminderDelay || currentSettings.reminderDelay,
      maxReminders: 1, // Always fixed to 1 reminder only
      emailEnabled: newSettings.emailEnabled !== undefined ? newSettings.emailEnabled : currentSettings.emailEnabled,
      autoSendReviews: newSettings.autoSendReviews !== undefined ? newSettings.autoSendReviews : currentSettings.autoSendReviews
    };

    // Save to localStorage (in a real app, this would save to database)
    if (typeof window !== 'undefined') {
      localStorage.setItem('reviewSettings', JSON.stringify(updatedSettings));
    }

    console.log('✅ Reminder settings updated:', updatedSettings);
    return updatedSettings;
  }

  // Process pending reminders
  async processReminders() {
    try {
      console.log('🔄 Processing pending reminders...');
      
      const pendingReminders = await reviewService.getPendingReminders();
      
      if (!pendingReminders.success) {
        console.error('Failed to get pending reminders:', pendingReminders.error);
        return { success: false, error: pendingReminders.error };
      }

      const reminders = pendingReminders.data || [];
      console.log(`📋 Found ${reminders.length} pending reminders`);

      const processed = [];
      const errors = [];

      for (const reminder of reminders) {
        try {
          console.log(`📧 Sending reminder to ${reminder.customer_email}...`);
          
          // Send reminder email
          const emailResult = await emailService.sendReviewReminder(
            reminder.request_id,
            {
              email: reminder.customer_email,
              name: reminder.customer_name
            },
            this.getBusinessSettings()
          );

          if (emailResult.success) {
            processed.push({
              requestId: reminder.request_id,
              customerEmail: reminder.customer_email,
              status: 'sent'
            });
            console.log(`✅ Reminder sent to ${reminder.customer_email}`);
          } else {
            errors.push({
              requestId: reminder.request_id,
              customerEmail: reminder.customer_email,
              error: emailResult.error
            });
            console.error(`❌ Failed to send reminder to ${reminder.customer_email}:`, emailResult.error);
          }
        } catch (error) {
          errors.push({
            requestId: reminder.request_id,
            customerEmail: reminder.customer_email,
            error: error.message
          });
          console.error(`❌ Error processing reminder for ${reminder.customer_email}:`, error);
        }
      }

      console.log(`✅ Processed ${processed.length} reminders, ${errors.length} errors`);

      return {
        success: true,
        processed: processed.length,
        errors: errors.length,
        details: { processed, errors }
      };
    } catch (error) {
      console.error('Error processing reminders:', error);
      return { success: false, error: 'Failed to process reminders' };
    }
  }

  // Get review status for customer in CRM
  async getCustomerReviewStatus(customerId) {
    try {
      const { data: reviewRequests, error } = await supabase
        .from('review_requests')
        .select('*')
        .eq('customer_id', customerId)
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('Error fetching review status:', error);
        return { success: false, error: error.message };
      }

      if (!reviewRequests || reviewRequests.length === 0) {
        return { 
          success: true, 
          data: { 
            status: 'none', 
            lastRequest: null 
          } 
        };
      }

      const lastRequest = reviewRequests[0];
      
      let status = 'pending';
      if (lastRequest.review_completed) {
        status = 'completed';
      } else if (lastRequest.status === 'failed') {
        status = 'failed';
      } else if (lastRequest.reminder_count >= lastRequest.max_reminders) {
        status = 'expired';
      }

      return {
        success: true,
        data: {
          status,
          lastRequest,
          totalRequests: reviewRequests.length,
          rating: lastRequest.review_rating
        }
      };
    } catch (error) {
      console.error('Error getting customer review status:', error);
      return { success: false, error: 'Failed to get review status' };
    }
  }

  // Process scheduled review requests and send emails when due
  async processScheduledReviewRequests() {
    try {
      console.log('🔄 Processing scheduled review requests...');
      
      // Get all scheduled review requests that are due to be sent
      const { data: scheduledRequests, error: fetchError } = await supabase
        .from('review_requests')
        .select('*')
        .eq('status', 'scheduled')
        .lte('scheduled_send_at', new Date().toISOString())
        .order('scheduled_send_at', { ascending: true });

      if (fetchError) {
        console.error('❌ Error fetching scheduled requests:', fetchError);
        return { success: false, error: fetchError.message };
      }

      if (!scheduledRequests || scheduledRequests.length === 0) {
        console.log('📋 No scheduled review requests due for sending');
        return { success: true, processed: 0 };
      }

      console.log(`📧 Found ${scheduledRequests.length} scheduled review requests due for sending`);

      let successCount = 0;
      let failureCount = 0;

      // Process each scheduled request
      for (const request of scheduledRequests) {
        try {
          console.log(`📤 Processing scheduled request ${request.id} for customer ${request.customer_id}`);
          
          // Get customer details
          const { data: customer, error: customerError } = await supabase
            .from('contacts')
            .select('*')
            .eq('id', request.customer_id)
            .single();

          if (customerError || !customer) {
            console.error(`❌ Customer not found for request ${request.id}:`, customerError);
            await this.markRequestAsFailed(request.id, 'Customer not found');
            failureCount++;
            continue;
          }

          if (!customer.email) {
            console.error(`❌ Customer ${request.customer_id} has no email address`);
            await this.markRequestAsFailed(request.id, 'Customer has no email address');
            failureCount++;
            continue;
          }

          // Create unique review token
          const tokenResult = await reviewService.createReviewToken(
            request.id,
            request.customer_id,
            customer.email
          );

          if (!tokenResult.success) {
            console.error(`❌ Failed to create review token for request ${request.id}:`, tokenResult.error);
            await this.markRequestAsFailed(request.id, 'Failed to create review token');
            failureCount++;
            continue;
          }

          const reviewToken = tokenResult.data.token;

          // Get current user's reply-to email
          const { data: { user } } = await supabase.auth.getUser();
          let replyToEmail = null;
          
          if (user) {
            const { data: userProfile } = await supabase
              .from('user_profiles')
              .select('reply_to_email, email')
              .eq('id', user.id)
              .single();
            
            replyToEmail = userProfile?.reply_to_email || userProfile?.email;
          }

          // ✅ Enhanced: Choose email method based on trigger type
          let emailResult;
          
          if (request.reminder_count && request.reminder_count > 0) {
            // This is an automatic follow-up, use reminder email
            console.log(`📧 Sending follow-up reminder for request ${request.id}`);
            emailResult = await emailService.sendReviewReminder(
              request.id,
              {
                email: customer.email,
                name: customer.name || customer.first_name + ' ' + customer.last_name
              },
              this.getBusinessSettings()
            );
          } else {
            // This is an initial request, use regular review email with campaign configuration
            console.log(`📧 Sending initial review request for request ${request.id}`);
            emailResult = await emailService.sendReviewEmail({
              customerId: request.customer_id,
              customerEmail: customer.email,
              customerName: customer.name || customer.first_name + ' ' + customer.last_name,
              reviewToken: reviewToken,
              replyToEmail: replyToEmail,
              // ✅ NEW: Use saved campaign configuration from request
              campaignConfig: request.campaign_config || {},
              appointmentData: {
                service_type: 'Service Completion',
                scheduled_date: new Date().toISOString(),
                customer_name: customer.name || customer.first_name + ' ' + customer.last_name,
                customer_email: customer.email,
                stage_name: request.pipeline_stage
              }
            });
          }

          // Update request status based on email result
          const updateData = {
            status: emailResult.success ? 'submitted' : 'failed',
            sent_at: new Date().toISOString(),
            error_message: emailResult.success ? null : emailResult.error
          };

          if (emailResult.emailId) {
            updateData.email_id = emailResult.emailId;
          }

          await supabase
            .from('review_requests')
            .update(updateData)
            .eq('id', request.id);

          if (emailResult.success) {
            console.log(`✅ Scheduled review request sent successfully for customer ${request.customer_id} (${customer.email})`);
            successCount++;
          } else {
            console.error(`❌ Failed to send scheduled review request for customer ${request.customer_id}:`, emailResult.error);
            failureCount++;
          }

        } catch (error) {
          console.error(`❌ Error processing scheduled request ${request.id}:`, error);
          await this.markRequestAsFailed(request.id, error.message);
          failureCount++;
        }
      }

      console.log(`📊 Processed ${scheduledRequests.length} scheduled requests: ${successCount} sent, ${failureCount} failed`);

      return {
        success: true,
        processed: scheduledRequests.length,
        sent: successCount,
        failed: failureCount
      };

    } catch (error) {
      console.error('❌ Error processing scheduled review requests:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper function to mark a request as failed
  async markRequestAsFailed(requestId, errorMessage) {
    try {
      await supabase
        .from('review_requests')
        .update({
          status: 'failed',
          error_message: errorMessage,
          sent_at: new Date().toISOString()
        })
        .eq('id', requestId);
    } catch (error) {
      console.error(`❌ Error marking request ${requestId} as failed:`, error);
    }
  }

  // Get automation settings from organization or user preferences
  async checkAutomationSettings(organizationId) {
    try {
      // Use existing business settings system (localStorage-based)
      const settings = this.getBusinessSettings();
      
      // Map existing settings to our new structure
      const automationSettings = {
        autoSendReviews: settings.autoSendReviews || true,
        reminderAutomation: settings.autoSendReminders !== false, // Default to true if not set
        reminderDelay: settings.reminderDelay || 72, // hours after first email
        expireAfter: 30 * 24 // 30 days in hours (hardcoded for now)
      };
      
      return {
        success: true,
        settings: automationSettings
      };
    } catch (error) {
      console.error('Error checking automation settings:', error);
      return {
        success: false,
        error: 'Failed to check automation settings',
        settings: {
          autoSendReviews: true,
          reminderAutomation: true,
          reminderDelay: 72,
          expireAfter: 30 * 24
        }
      };
    }
  }

  // Enhanced review status that includes follow-up state and timing
  async getDetailedReviewStatus(customerId, organizationId) {
    try {
      const { data: reviewRequests, error } = await supabase
        .from('review_requests')
        .select('*')
        .eq('customer_id', customerId)
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('Error fetching detailed review status:', error);
        return { success: false, error: error.message };
      }

      // Get automation settings
      const automationResult = await this.checkAutomationSettings(organizationId);
      const automationSettings = automationResult.settings;

      if (!reviewRequests || reviewRequests.length === 0) {
        return { 
          success: true, 
          data: { 
            status: 'none', 
            lastRequest: null,
            automationSettings,
            timeUntilAction: null,
            actionAvailable: true
          } 
        };
      }

      const lastRequest = reviewRequests[0];
      const now = new Date();
      const sentAt = new Date(lastRequest.sent_at);
      const hoursSinceSent = (now - sentAt) / (1000 * 60 * 60);

      let status = 'pending';
      let timeUntilAction = null;
      let actionAvailable = true;

      // Determine current status
      if (lastRequest.review_completed) {
        status = 'completed';
        actionAvailable = false;
      } else if (lastRequest.status === 'failed') {
        status = 'failed';
        actionAvailable = true;
      } else if (lastRequest.status === 'expired') {
        // ✅ Enhanced: Handle explicitly expired requests
        status = 'expired';
        actionAvailable = true;
      } else if (lastRequest.reminder_count > 0) {
        // Follow-up has been sent
        status = 'follow_up_sent';
        actionAvailable = false;
        
        // Check if it should expire
        const hoursUntilExpire = automationSettings.expireAfter - hoursSinceSent;
        if (hoursUntilExpire <= 0) {
          status = 'expired';
          actionAvailable = true;
        }
      } else if (lastRequest.status === 'submitted') {
        // Initial request sent, check if follow-up is due
        status = 'submitted';
        
        if (automationSettings.reminderAutomation) {
          const hoursUntilFollowUp = automationSettings.reminderDelay - hoursSinceSent;
          if (hoursUntilFollowUp <= 0) {
            // Follow-up is due - this should trigger automatic sending
            status = 'follow_up_due';
            actionAvailable = false;
          } else {
            timeUntilAction = Math.ceil(hoursUntilFollowUp);
            actionAvailable = false;
          }
        } else {
          // Manual follow-up mode
          actionAvailable = true;
        }
      }

      return {
        success: true,
        data: {
          status,
          lastRequest,
          totalRequests: reviewRequests.length,
          rating: lastRequest.review_rating,
          automationSettings,
          timeUntilAction,
          actionAvailable,
          hoursSinceSent: Math.floor(hoursSinceSent)
        }
      };
    } catch (error) {
      console.error('Error getting detailed review status:', error);
      return { success: false, error: 'Failed to get detailed review status' };
    }
  }

  // Determine button state based on review status and automation settings
  async determineButtonState(customerId, organizationId) {
    try {
      const statusResult = await this.getDetailedReviewStatus(customerId, organizationId);
      
      if (!statusResult.success) {
        return {
          success: false,
          error: statusResult.error
        };
      }

      const { status, timeUntilAction, actionAvailable, automationSettings } = statusResult.data;

      let buttonConfig = {
        enabled: true,
        text: 'Request Review',
        action: 'send_initial',
        showCountdown: false,
        countdownHours: null,
        variant: 'outline'
      };

      switch (status) {
        case 'none':
        case 'failed':
        case 'expired':
          buttonConfig = {
            enabled: true,
            text: status === 'expired' ? 'Request Review Again' : 'Request Review',
            action: 'send_initial',
            showCountdown: false,
            countdownHours: null,
            variant: 'outline'
          };
          break;

        case 'submitted':
          if (automationSettings.reminderAutomation) {
            // Automation is ON - show countdown
            buttonConfig = {
              enabled: false,
              text: timeUntilAction > 0 ? `Auto follow-up in ${timeUntilAction}h` : 'Sending follow-up...',
              action: 'none',
              showCountdown: true,
              countdownHours: timeUntilAction,
              variant: 'outline'
            };
          } else {
            // Automation is OFF - manual follow-up
            buttonConfig = {
              enabled: true,
              text: 'Send Follow Up',
              action: 'send_followup',
              showCountdown: false,
              countdownHours: null,
              variant: 'outline'
            };
          }
          break;

        case 'follow_up_sent':
          buttonConfig = {
            enabled: false,
            text: 'Follow Up Sent',
            action: 'none',
            showCountdown: false,
            countdownHours: null,
            variant: 'outline'
          };
          break;

        case 'completed':
          buttonConfig = {
            enabled: false,
            text: 'Review Complete',
            action: 'none',
            showCountdown: false,
            countdownHours: null,
            variant: 'ghost'
          };
          break;

        case 'follow_up_due':
          buttonConfig = {
            enabled: false,
            text: 'Sending follow-up...',
            action: 'none',
            showCountdown: false,
            countdownHours: null,
            variant: 'outline'
          };
          break;
      }

      return {
        success: true,
        buttonConfig,
        statusData: statusResult.data
      };
    } catch (error) {
      console.error('Error determining button state:', error);
      return {
        success: false,
        error: 'Failed to determine button state'
      };
    }
  }

  // ==========================
  // INSTANT REQUEST (email-only)
  // ==========================
  async createInstantRequest({ name, email, phone, serviceName = null, serviceId = null, organizationId = null }) {
    try {
      if (!email || !name) {
        return { success: false, error: 'Name and email are required' };
      }

      // Resolve organization
      let orgId = organizationId;
      if (!orgId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { success: false, error: 'Not authenticated' };
        const { data: orgData, error: orgErr } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        if (orgErr || !orgData?.organization_id) return { success: false, error: 'Organization not found' };
        orgId = orgData.organization_id;
      }

      // Upsert or create contact by email within org
      const { data: existing } = await supabase
        .from('contacts')
        .select('*')
        .eq('organization_id', orgId)
        .eq('email', email)
        .single();

      let contactId;
      if (existing) {
        const { data: updated, error: updErr } = await supabase
          .from('contacts')
          .update({ name, phone })
          .eq('id', existing.id)
          .select()
          .single();
        if (updErr) return { success: false, error: updErr.message };
        contactId = updated.id;
      } else {
        const { data: created, error: createErr } = await supabase
          .from('contacts')
          .insert([{ organization_id: orgId, name, email, phone, customer_type: 'individual', pipeline_stage: 'Lead' }])
          .select()
          .single();
        if (createErr) return { success: false, error: createErr.message };
        contactId = created.id;
      }

      // Determine review stage (triggers_review)
      const stages = await customerService.getReviewTriggerStages(orgId);
      const reviewStageName = stages.length > 0 ? stages[0].name : 'Review Requested';
      await customerService.updateContactStage(contactId, reviewStageName);

      // Load campaign configuration
      let campaignData = await campaignService.getCampaign(orgId);
      if (!campaignData) campaignData = campaignService.getDefaultCampaign();

      // Create review request (pending)
      const { data: request, error: reqErr } = await supabase
        .from('review_requests')
        .insert([{
          organization_id: orgId,
          customer_id: contactId,
          pipeline_stage: reviewStageName,
          trigger_type: 'manual',
          status: 'pending',
          method: 'email',
          recipient_email: email,
          sent_at: new Date().toISOString(),
          campaign_id: campaignData.id || null,
          campaign_config: campaignData,
          notes: serviceName ? `Instant request for service: ${serviceName}` : 'Instant request'
        }])
        .select()
        .single();
      if (reqErr) return { success: false, error: reqErr.message };

      // Create review token
      const tokenResult = await reviewService.createReviewToken(request.id, contactId, email);
      if (!tokenResult.success) return { success: false, error: tokenResult.error };

      // Reply-to from user profile
      const { data: { user } } = await supabase.auth.getUser();
      let replyToEmail = null;
      if (user) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('reply_to_email, email')
          .eq('id', user.id)
          .single();
        replyToEmail = userProfile?.reply_to_email || userProfile?.email;
      }

      // Send email now
      const emailResult = await emailService.sendReviewEmail({
        customerId: contactId,
        customerEmail: email,
        customerName: name,
        reviewToken: tokenResult.data.token,
        replyToEmail,
        campaignConfig: campaignData,
        appointmentData: { service_type: serviceName || 'Service', scheduled_date: new Date().toISOString(), customer_name: name, customer_email: email, stage_name: reviewStageName }
      });

      // Update request status based on email result
      await supabase
        .from('review_requests')
        .update({ status: emailResult.success ? 'submitted' : 'failed' })
        .eq('id', request.id);

      return { success: true, contactId, reviewRequestId: request.id };
    } catch (error) {
      console.error('createInstantRequest error:', error);
      return { success: false, error: error.message };
    }
  }
}

const reviewAutomationService = new ReviewAutomationService();
export default reviewAutomationService; 